import { useEffect } from 'react';
import { config, ThemeName, themeNames } from 'appConfig';
import { PermissionNode } from '__generated__/graphql';
import { permissionsVar, settingsVar, userThemeVar } from 'client/cache';
import { FrontendSettingNode } from 'typeDeclarations/graphql/nodes';
import { AppSettingsMap } from 'utils/Settings/types';
import { isAppSetting } from 'utils/Settings/utils';
import { parseJSONScalar } from 'utils/parseJsonScalar';
import { useGetSessionData } from 'hooks/queries/useGetSessionData';
import { MAX_SETTINGS } from 'utils/Settings/constants';
import { NonPaginatedEdge } from 'typeDeclarations/graphql/base-types';
import { APIPermission } from 'typeDeclarations/enums';
import { logError } from 'utils/logging';

// We might be able to optimize this with a better architecture.
export const useSetupApp = () => {
  const { data, loading } = useGetSessionData();

  const setupPermissions = (permissions: Pick<PermissionNode, 'id' | '_id'>[]) => {
    permissionsVar(
      permissions.reduce<PartialRecord<APIPermission, boolean>>((acc, permissionNode) => {
        acc[permissionNode._id as APIPermission] = true;

        return acc;
      }, {}),
    );
  };

  const setupSettings = (
    settings: NonPaginatedEdge<Pick<FrontendSettingNode, 'id' | 'name' | '__typename' | 'modified' | 'value'>>[],
  ) => {
    settingsVar(
      settings.reduce<AppSettingsMap>((acc, edge) => {
        const { name, value } = edge.node;

        if (isAppSetting(name)) {
          acc[name] = parseJSONScalar(value);
        }

        return acc;
      }, {}),
    );
  };

  const setupTheme = (appTheme: ThemeName, userTheme: string | null) => {
    const theme = userTheme || appTheme;

    // If the theme is the default or the leadzai theme, we don't need to change it.
    // Note: "advertio" and "leadzai" are from the back-end, but the front-end treats both as "default".
    if (theme === 'default' || theme === 'advertio' || theme === 'leadzai') {
      userThemeVar('default');
      return;
    }

    if (theme in themeNames) {
      userThemeVar(theme as ThemeName);
    }
  };

  useEffect(() => {
    if (!data) return;

    const { getContextPermissions, sessionUser, sessionUserTeamRelation } = data;

    setupTheme(config.theme, sessionUser.theme);
    setupPermissions(getContextPermissions);

    if (!sessionUserTeamRelation) return;

    const {
      frontendSettings: { edges, pageInfo },
    } = sessionUserTeamRelation;

    if (pageInfo.hasNextPage) {
      logError(
        new Error(
          `The user ${sessionUser.id} has more settings saved than expected. The current maximum amount is ${MAX_SETTINGS}`,
        ),
        {
          level: 'warning',
          tags: { context: 'Settings' },
          fingerprint: ['{{ default }}', sessionUser.id],
          extras: { settingsCount: edges.length, maxSettings: MAX_SETTINGS },
        },
      );
    }

    setupSettings(edges);
  }, [data]);

  return { loading };
};
